import {z} from "zod/v4-mini"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {MeshNodeDefinition, MeshNodeParameters} from "#template/nodes/object/mesh/types"
import {MeshNode} from "#template/nodes/object/mesh/mesh-node"
import {meshNodeParameters} from "#template/nodes/object/mesh/validation"
import {DataObjectReference} from "#template/nodes/image/data-object-reference"
import {positionValue} from "#template/nodes/validation"
import type {Evaluator} from "@cm/graph-v3"
import {graphNode} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import type {PositionValue} from "#template/nodes/types"
import type {OnCompileContext, TemplateNodeContext, VisitorNodeResult} from "#template/template-node/types"
import {LoadMesh} from "#template/runtime-graph/nodes/load-mesh"
import {LoadData} from "#template/runtime-graph/nodes/load-data"
import {isEvaluatedTemplateNode} from "#template/utils/template-node-utils"
import {visitNone} from "#template/template-node/visit-helpers"

export type StoredMeshParameters = {
    name: string
    drcDataObject: DataObjectReference
    plyDataObject: DataObjectReference
    metaData?: {
        dracoBitDepth?: number
        dracoResolution?: number
        osdUseRenderIterations?: number
        osdRenderIterations?: number
        defaultPosition?: PositionValue
        centered?: boolean
        exporterVersion?: string
    }
} & MeshNodeParameters

const storedMeshDefinition: MeshNodeDefinition<StoredMeshParameters> = {
    nodeClass: "StoredMesh",
    validation: {
        parameters: z.extend(
            z.object({
                name: z.string(),
                drcDataObject: graphNode(DataObjectReference),
                plyDataObject: graphNode(DataObjectReference),
                metaData: z.optional(
                    z.object({
                        dracoBitDepth: z.optional(z.number()),
                        dracoResolution: z.optional(z.number()),
                        osdUseRenderIterations: z.optional(z.number()),
                        osdRenderIterations: z.optional(z.number()),
                        defaultPosition: z.optional(positionValue),
                        centered: z.optional(z.boolean()),
                        exporterVersion: z.optional(z.string()),
                    }),
                ),
            }),
            meshNodeParameters.shape,
        ),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class StoredMesh extends MeshNode<StoredMeshParameters> {
    public override get definition() {
        return storedMeshDefinition
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<StoredMeshParameters> {
        const {evaluator} = context
        const {templateContext, activeNodeSet} = evaluator
        const {sceneManager} = templateContext
        const {subdivisionRenderIterations, plyDataObject, drcDataObject} = this.parameters

        const computeDummyReifiedMeshData = templateContext.computeDummyReifiedMeshData && !isEvaluatedTemplateNode(this, activeNodeSet)

        const scope = evaluator.getScope(this)
        const {completeMeshData} = scope.node(LoadMesh, {
            sceneManager,
            computeDummyReifiedMeshData,
            data: computeDummyReifiedMeshData
                ? null
                : scope.node(LoadData, {
                      sceneManager,
                      dataObject: drcDataObject.getDataObject(scope, evaluator),
                  }).data,
            drcDataObjectId: drcDataObject.parameters.dataObjectId,
            plyDataObjectId: plyDataObject.parameters.dataObjectId,
            displaySubdivisionLevel: this.getDisplaySubdivisionLevel(context),
            renderSubdivisionLevel: subdivisionRenderIterations ?? 0,
        })

        this.setupMesh(scope, context, completeMeshData, false)

        return visitNone(this.parameters)
    }
}
